"use client";

import { useEffect, useState } from "react";
import { Camera, ChevronRight } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/hooks/useStore";
import { getOrganizationList } from "@/store/controller/organization.controller";
import { getTestimonialList } from "@/store/controller/testimonial.controller";
import { getContactList } from "@/store/controller/contact.controller";
import { getIcon } from "./iconMap";
import { ORG_THEME } from "./theme";

export function OrganisasiSection() {
  const dispatch = useAppDispatch();
  const organizations = useAppSelector((state) => state.organization.list);
  const testimonials = useAppSelector((state) => state.testimonial.list);
  const contact = useAppSelector((state) => state.contact.list[0]);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const alumniInstagramLabel = contact?.alumni_instagram_label || "Ikuti IG Alumni";
  const alumniInstagramHref = contact?.alumni_instagram_href || "#";

  useEffect(() => {
    dispatch(getOrganizationList({ limit: 100 }));
    dispatch(getTestimonialList({}));
    dispatch(getContactList({}));
  }, [dispatch]);

  useEffect(() => {
    if (testimonials.length === 0) return;
    const timer = setInterval(() => setActiveTestimonial((current) => (current + 1) % testimonials.length), 4000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  return (
    <section id="organisasi" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <span className="inline-block text-green-700 font-semibold text-sm uppercase tracking-widest mb-3">
              Ekstrakulikuler
            </span>
            <h2 className="text-3xl font-extrabold text-gray-900 mb-2">
              Organisasi <span className="text-green-700">Siswa</span>
            </h2>
            <div className="w-14 h-1 bg-yellow-400 rounded mb-6" />
            <p className="text-gray-500 text-sm leading-relaxed mb-8">
              Beragam organisasi dan ekstrakurikuler untuk mengembangkan potensi dan karakter siswa secara
              menyeluruh.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
              {organizations.map((org) => {
                const Icon = getIcon(org.icon);
                const theme = ORG_THEME[org.color_theme] ?? ORG_THEME.green;
                const cardClass =
                  "block p-4 bg-white rounded-2xl border border-gray-100 hover:border-green-200 hover:shadow-md transition-all text-center group";
                const content = (
                  <>
                    <div
                      className={`w-10 h-10 rounded-xl ${theme.bg} flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform`}
                    >
                      <Icon size={20} className={theme.iconColor} />
                    </div>
                    <div className="font-bold text-gray-900 text-sm group-hover:text-green-700 transition-colors">{org.name}</div>
                    <div className="text-gray-400 text-xs mt-0.5">{org.description}</div>
                  </>
                );

                if (!org.link) {
                  return (
                    <div key={org.id} className={cardClass}>
                      {content}
                    </div>
                  );
                }

                const isExternal = /^https?:\/\//i.test(org.link);
                return (
                  <a
                    key={org.id}
                    href={org.link}
                    className={cardClass}
                    {...(isExternal && { target: "_blank", rel: "noopener noreferrer" })}
                  >
                    {content}
                  </a>
                );
              })}
            </div>
          </div>

          <div id="alumni">
            <span className="inline-block text-green-700 font-semibold text-sm uppercase tracking-widest mb-3">
              Testimoni
            </span>
            <h2 className="text-3xl font-extrabold text-gray-900 mb-2">
              Kisah <span className="text-green-700">Alumni</span>
            </h2>
            <div className="w-14 h-1 bg-yellow-400 rounded mb-6" />
            <p className="text-gray-500 text-sm leading-relaxed mb-8">
              Ribuan alumni SMAIT Ihsanul Fikri kini berkarya di berbagai universitas dan lembaga terkemuka.
            </p>

            {testimonials.length > 0 && (
              <div className="relative">
                <div className="overflow-hidden rounded-3xl border border-gray-100 shadow-sm bg-white">
                  <div
                    className="flex transition-transform duration-500 ease-in-out"
                    style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}
                  >
                    {testimonials.map((testimonial, testimonialIndex) => (
                      <div key={testimonialIndex} className="w-full flex-shrink-0 p-7">
                        <div className="text-green-600 text-5xl font-serif leading-none mb-3">&ldquo;</div>
                        <p className="text-gray-700 leading-relaxed text-sm mb-6 italic">{testimonial.description}</p>
                        <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                          {testimonial.image ? (
                            <img
                              src={testimonial.image}
                              alt={testimonial.name}
                              className="w-12 h-12 rounded-full object-cover flex-shrink-0 ring-2 ring-green-100"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 text-green-700 font-bold text-lg">
                              {testimonial.name.charAt(0)}
                            </div>
                          )}
                          <div>
                            <div className="font-bold text-gray-900 text-sm">{testimonial.name}</div>
                            <div className="text-green-700 text-xs font-medium">{testimonial.university}</div>
                            <div className="text-gray-400 text-xs">{testimonial.batch}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between mt-5">
                  <div className="flex gap-2">
                    {testimonials.map((_, dotIndex) => (
                      <button
                        key={dotIndex}
                        onClick={() => setActiveTestimonial(dotIndex)}
                        className={`h-2 rounded-full transition-all duration-300 ${
                          dotIndex === activeTestimonial ? "w-6 bg-green-700" : "w-2 bg-gray-300 hover:bg-gray-400"
                        }`}
                      />
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() =>
                        setActiveTestimonial((previousIndex) => (previousIndex - 1 + testimonials.length) % testimonials.length)
                      }
                      className="w-9 h-9 rounded-full border border-gray-200 hover:border-green-700 hover:bg-green-50 flex items-center justify-center transition-all text-gray-500 hover:text-green-700"
                    >
                      <ChevronRight size={16} className="rotate-180" />
                    </button>
                    <button
                      onClick={() => setActiveTestimonial((previousIndex) => (previousIndex + 1) % testimonials.length)}
                      className="w-9 h-9 rounded-full border border-gray-200 hover:border-green-700 hover:bg-green-50 flex items-center justify-center transition-all text-gray-500 hover:text-green-700"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            <a
              href={alumniInstagramHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border-2 border-green-700 text-green-700 hover:bg-green-700 hover:text-white font-semibold px-6 py-3 rounded-xl transition-all text-sm mt-6"
            >
              <Camera size={16} />
              {alumniInstagramLabel}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
